<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import ShareService, { ROLE_LABELS, ASSIGNABLE_ROLES } from "../../services/shareService.js";
import { useTopBar } from "../../composables/useTopBar.js";
import { useShareModal } from "../../composables/useShareModal.js";
import { useToast } from "../../composables/useToast.js";
import { formatRelativeDateTime } from "../../composables/useDateFormat.js";
import Spinner from "./Spinner.vue";

const emit = defineEmits(["close"]);
const route = useRoute();
const { title } = useTopBar();
const { overrideContext } = useShareModal();
const shareService = new ShareService();
const toast = useToast();

function close() { emit("close"); }
function onEsc(e) { if (e.key === "Escape") close(); }
onMounted(() => document.addEventListener("keydown", onEsc));
onBeforeUnmount(() => document.removeEventListener("keydown", onEsc));

// Sharing is project-level only (the backend has no document-level sharing) —
// a card's menu can pass an explicit project via overrideContext; otherwise a
// document route still carries its parent project's id in route.params.
const resourceId = computed(() => overrideContext.value?.resourceId ?? route.params.projectId ?? null);
const resourceName = ref(overrideContext.value?.resourceName ?? title.value ?? "Project");

// Opened from the header's Share button (anchorRect set): pop up near that
// button instead of centered — `position: fixed` here takes the modal out of
// the backdrop's centering flex layout entirely, so no other class changes
// are needed. Opened from a card's kebab menu (no anchorRect): stays centered.
const POPOVER_GAP = 8;
const popoverStyle = computed(() => {
  const rect = overrideContext.value?.anchorRect;
  if (!rect) return {};
  return {
    position: "fixed",
    top: `${rect.bottom + POPOVER_GAP}px`,
    right: `${Math.max(8, window.innerWidth - rect.right)}px`,
    // Overrides the max-h-[85vh] class — that's sized for a centered modal;
    // anchored below the button there's less room beneath it, so cap to
    // whatever's actually left before the viewport bottom.
    maxHeight: `${Math.max(200, window.innerHeight - rect.bottom - POPOVER_GAP * 2)}px`,
  };
});

function initials(name) {
  const parts = (name ?? "").trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
}

// ─── Load access ────────────────────────────────────────────────────────────
const isLoading = ref(true);
const collaborators = ref([]);

async function load() {
  isLoading.value = true;
  try {
    const data = await shareService.getAccess({ projectId: resourceId.value });
    collaborators.value = data.collaborators;
    // Explicit overrides (e.g. ProjectCardMenu already knows the name) win —
    // otherwise use whatever the backend calls this project.
    if (!overrideContext.value?.resourceName && data.projectName) resourceName.value = data.projectName;
  } catch (error) {
    toast({ type: "error", title: "Couldn't load collaborators", message: error.message });
  } finally {
    isLoading.value = false;
  }
}
onMounted(load);

// ─── Invite (multiple emails) ───────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const pendingEmails = ref([]); // committed chips
const inviteDraft = ref(""); // text currently being typed
const inviteDraftInput = ref(null);
const inviteRole = ref("VIEWER");
const isInviting = ref(false);
const showInviteRoleMenu = ref(false);

function parseEmails(text) {
  return text
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter((e) => e && EMAIL_RE.test(e));
}

const hasPendingInvite = computed(() => pendingEmails.value.length > 0 || parseEmails(inviteDraft.value).length > 0);

function addEmails(text) {
  for (const email of parseEmails(text)) {
    if (!pendingEmails.value.includes(email)) pendingEmails.value.push(email);
  }
}

function commitDraft() {
  if (!inviteDraft.value.trim()) return;
  addEmails(inviteDraft.value);
  inviteDraft.value = "";
}

function onDraftKeydown(e) {
  if (e.key === "," || e.key === ";") {
    e.preventDefault();
    commitDraft();
  } else if (e.key === "Backspace" && !inviteDraft.value && pendingEmails.value.length) {
    pendingEmails.value.pop();
  }
}

function onDraftPaste(e) {
  const text = e.clipboardData?.getData("text") ?? "";
  if (/[\s,;]/.test(text)) {
    e.preventDefault();
    addEmails(text);
  }
}

function removePendingEmail(email) {
  pendingEmails.value = pendingEmails.value.filter((e) => e !== email);
}

// Invites each email independently and reports partial failure the same way
// the legacy dialog does — succeeded emails drop off the pending list and
// join the collaborator list, failed ones stay pending so the user can retry,
// and every failure reason is surfaced in one aggregated toast.
async function invite() {
  commitDraft();
  if (!pendingEmails.value.length || isInviting.value) return;
  isInviting.value = true;
  const emails = [...pendingEmails.value];
  try {
    const results = await Promise.allSettled(
      emails.map((email) => shareService.invite({ projectId: resourceId.value, email, role: inviteRole.value }))
    );
    const errors = [];
    results.forEach((result, i) => {
      if (result.status === "fulfilled") {
        collaborators.value.push(result.value);
        pendingEmails.value = pendingEmails.value.filter((e) => e !== emails[i]);
      } else {
        errors.push(`${emails[i]}: ${result.reason?.message ?? "Failed to invite"}`);
      }
    });
    if (errors.length) {
      toast({ type: "error", title: "Error sending invites", message: errors.join("\n") });
    }
  } finally {
    isInviting.value = false;
  }
}

// ─── Per-collaborator role menu ─────────────────────────────────────────────
const openRoleMenuFor = ref(null);
function toggleRoleMenu(id) {
  openRoleMenuFor.value = openRoleMenuFor.value === id ? null : id;
}
async function setCollaboratorRole(collaborator, role) {
  openRoleMenuFor.value = null;
  const previousRole = collaborator.role;
  collaborator.role = role; // optimistic
  try {
    await shareService.updateCollaboratorRole({ projectId: resourceId.value, email: collaborator.email, role });
  } catch (error) {
    collaborator.role = previousRole;
    toast({ type: "error", title: "Couldn't update role", message: error.message });
  }
}
async function removeCollaborator(collaborator) {
  openRoleMenuFor.value = null;
  const previous = collaborators.value;
  collaborators.value = collaborators.value.filter((c) => c.id !== collaborator.id); // optimistic
  try {
    await shareService.removeCollaborator({ projectId: resourceId.value, email: collaborator.email });
  } catch (error) {
    collaborators.value = previous;
    toast({ type: "error", title: "Couldn't remove collaborator", message: error.message });
  }
}

// ─── Resend invite ──────────────────────────────────────────────────────────
const resendingFor = ref(null);
async function resendInvite(collaborator) {
  resendingFor.value = collaborator.id;
  try {
    await shareService.resendInvite({ projectId: resourceId.value, email: collaborator.email, role: collaborator.role });
    toast({ type: "success", title: "Invitation resent", message: `Sent to ${collaborator.email}` });
  } catch (error) {
    toast({ type: "error", title: "Couldn't resend invitation", message: error.message });
  } finally {
    resendingFor.value = null;
  }
}

function closeAllMenus() {
  openRoleMenuFor.value = null;
  showInviteRoleMenu.value = false;
}
</script>

<template>
  <div class="fixed inset-0 bg-black/20 dark:bg-black/50 z-[400] flex items-center justify-center p-4" @click.self="close">
    <div class="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[560px] max-h-[85vh] flex flex-col overflow-hidden z-[420]" :style="popoverStyle" @click="closeAllMenus">

      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 shrink-0">
        <h2 class="text-lg font-bold text-slate-800 dark:text-slate-100 m-0 truncate">Share {{ resourceName }}</h2>
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors shrink-0"
          @click="close"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="h-px bg-slate-200 dark:bg-slate-700 shrink-0" />

      <div v-if="isLoading" class="flex justify-center py-16"><Spinner size="26px" /></div>

      <!-- flex-1 min-h-0 on this wrapper and on the "People with access" block
           below: without min-h-0 a flex child won't shrink below its content
           size, so with many collaborators this would just grow past the
           viewport (min-h-0 is the standard flexbox fix for a scrollable flex
           child). Only that block scrolls — Invite/General access/Copy link
           stay put via shrink-0. -->
      <div v-else class="flex flex-col flex-1 min-h-0">

        <!-- Invite people -->
        <div class="px-6 py-5 shrink-0">
          <h3 class="text-[15px] font-bold text-slate-800 dark:text-slate-100 m-0 mb-3">Invite people</h3>
          <div class="flex items-start gap-2">
            <div
              class="flex-1 min-w-0 flex flex-wrap items-center gap-1.5 bg-slate-100 dark:bg-slate-800 rounded-xl px-3 py-2 min-h-[46px] cursor-text"
              @click="inviteDraftInput?.focus()"
            >
              <span
                v-for="email in pendingEmails"
                :key="email"
                class="inline-flex items-center gap-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg pl-2.5 pr-1.5 py-1 text-[13px] text-slate-700 dark:text-slate-200"
              >
                {{ email }}
                <button
                  class="flex items-center justify-center w-4 h-4 rounded text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 border-0 bg-transparent cursor-pointer shrink-0"
                  @click.stop="removePendingEmail(email)"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
                  </svg>
                </button>
              </span>
              <input
                ref="inviteDraftInput"
                v-model="inviteDraft"
                type="text"
                :placeholder="pendingEmails.length ? '' : 'Enter email addresses'"
                class="flex-1 min-w-[140px] bg-transparent border-0 py-1 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 outline-none"
                @keydown.enter.prevent="commitDraft"
                @keydown="onDraftKeydown"
                @paste="onDraftPaste"
                @blur="commitDraft"
              />
            </div>
            <div class="relative shrink-0" @click.stop>
              <button
                class="flex items-center gap-1 px-3 py-3 text-sm text-slate-600 dark:text-slate-300 bg-transparent border-0 cursor-pointer whitespace-nowrap"
                @click="showInviteRoleMenu = !showInviteRoleMenu"
              >
                {{ ROLE_LABELS[inviteRole] }}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
              <div
                v-if="showInviteRoleMenu"
                class="absolute top-[calc(100%+4px)] right-0 w-36 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1.5 z-[430] overflow-hidden"
              >
                <button
                  v-for="r in ASSIGNABLE_ROLES"
                  :key="r"
                  :class="inviteRole === r ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60'"
                  class="flex items-center w-full px-3 py-2 text-sm border-0 bg-transparent cursor-pointer transition-colors text-left"
                  @click="inviteRole = r; showInviteRoleMenu = false"
                >
                  {{ ROLE_LABELS[r] }}
                </button>
              </div>
            </div>
            <button
              :disabled="!hasPendingInvite || isInviting"
              class="shrink-0 whitespace-nowrap px-5 py-2.5 rounded-xl bg-slate-800 dark:bg-slate-700 text-white text-sm font-semibold hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              @click="invite"
            >
              {{ isInviting ? "Inviting…" : "Invite" }}
            </button>
          </div>
        </div>

        <div class="h-px bg-slate-200 dark:bg-slate-700 shrink-0" />

        <!-- People with access -->
        <div class="px-6 py-5 flex flex-col flex-1 min-h-0">
          <h3 class="text-[15px] font-bold text-slate-800 dark:text-slate-100 m-0 mb-3 shrink-0">People with access ({{ collaborators.length }})</h3>
          <div class="flex flex-col divide-y divide-slate-100 dark:divide-slate-800 overflow-y-auto min-h-0">
            <div v-for="c in collaborators" :key="c.id" class="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
              <div class="flex items-center justify-center w-10 h-10 min-w-[40px] rounded-full bg-[#7ba7d4] text-white text-sm font-semibold shrink-0">
                {{ initials(c.name) }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <p class="min-w-[40px] max-w-[220px] text-sm font-semibold text-slate-800 dark:text-slate-100 m-0 truncate">{{ c.name }}</p>

                  <span v-if="c.role === 'OWNER'" class="inline-flex items-center px-2.5 py-1 rounded-full border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-xs font-medium text-slate-500 dark:text-slate-400 shrink-0">Owner</span>
                  <div v-else class="relative shrink-0" @click.stop>
                    <button
                      class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 cursor-pointer transition-colors"
                      @click="toggleRoleMenu(c.id)"
                    >
                      {{ ROLE_LABELS[c.role] }}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </button>
                    <div
                      v-if="openRoleMenuFor === c.id"
                      class="absolute top-[calc(100%+4px)] left-0 w-40 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1.5 z-[430] overflow-hidden"
                    >
                      <button
                        v-for="r in ASSIGNABLE_ROLES"
                        :key="r"
                        :class="c.role === r ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60'"
                        class="flex items-center w-full px-3 py-2 text-sm border-0 bg-transparent cursor-pointer transition-colors text-left"
                        @click="setCollaboratorRole(c, r)"
                      >
                        {{ ROLE_LABELS[r] }}
                      </button>
                      <div class="h-px bg-slate-100 dark:bg-slate-700 my-1.5 mx-2" />
                      <button
                        class="flex items-center w-full px-3 py-2 text-sm text-red-500 border-0 bg-transparent cursor-pointer hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors text-left"
                        @click="removeCollaborator(c)"
                      >
                        Remove access
                      </button>
                    </div>
                  </div>
                </div>
                <p class="text-[13px] text-slate-400 dark:text-slate-500 m-0 truncate">{{ c.email }}</p>
              </div>

              <div class="flex flex-col items-end gap-1 shrink-0 w-[160px] pl-4 ml-1 border-l border-slate-200 dark:border-slate-700 text-right whitespace-nowrap">
                <span v-if="c.lastAccessedAt" class="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <span class="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                  Last accessed
                </span>
                <span v-else class="inline-flex items-center gap-1.5 text-xs text-red-400 dark:text-red-400">
                  <span class="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                  Not yet accessed
                </span>

                <span v-if="c.lastAccessedAt" class="text-sm text-slate-800 dark:text-slate-200">{{ formatRelativeDateTime(c.lastAccessedAt) }}</span>
                <button
                  v-else-if="c.role !== 'OWNER'"
                  :disabled="resendingFor === c.id"
                  class="text-sm text-blue-600 dark:text-blue-400 underline bg-transparent border-0 cursor-pointer p-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  @click.stop="resendInvite(c)"
                >
                  {{ resendingFor === c.id ? "Resending…" : "Resend invitation" }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="h-px bg-slate-200 dark:bg-slate-700 shrink-0" />

        <!-- General access — disabled until public link sharing ships on the backend -->
        <div class="px-6 py-5 shrink-0 opacity-50 cursor-not-allowed select-none" title="Coming soon">
          <div class="flex items-center gap-2 mb-3">
            <h3 class="text-[15px] font-bold text-slate-800 dark:text-slate-100 m-0">General access</h3>
            <span class="text-[11px] font-semibold text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 rounded-full px-2 py-0.5">Coming soon</span>
          </div>
          <div class="flex items-center gap-3">
            <div class="flex items-center justify-center w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="text-slate-500 dark:text-slate-400">
                <path d="M10.5 13.5L13.5 10.5M8 16l-2.5 2.5a3.54 3.54 0 0 1-5-5L3 11m18 2l2.5-2.5a3.54 3.54 0 0 0-5-5L16 8"
                  stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <button type="button" disabled class="flex items-center gap-1 text-sm font-semibold text-slate-800 dark:text-slate-100 bg-transparent border-0 cursor-not-allowed">
                Restricted
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
              <p class="text-[13px] text-slate-400 dark:text-slate-500 m-0 mt-0.5">Only people with access can open this</p>
            </div>
          </div>
        </div>

        <!-- Copy link — tied to public link sharing, disabled for the same reason -->
        <div class="px-6 pb-6 shrink-0">
          <button
            type="button"
            disabled
            title="Coming soon"
            class="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-slate-800 dark:bg-slate-700 text-white text-sm font-bold transition-colors opacity-50 cursor-not-allowed"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M10.5 13.5L13.5 10.5M8 16l-2.5 2.5a3.54 3.54 0 0 1-5-5L3 11m18 2l2.5-2.5a3.54 3.54 0 0 0-5-5L16 8"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Copy link
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
